<script setup>
import { computed, ref, watch } from 'vue'
import ModalBase from './ModalBase.vue'
import CalendarPicker from './CalendarPicker.vue'
import { compareCalendarDates, getTodayIsoDate } from '@/utils/calendarDate'

const todayIso = getTodayIsoDate()

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    title: {
        type: String,
        default: 'Datums kiezen',
    },
    modelValue: {
        type: Array,
        default: () => [],
    },
    helpText: {
        type: String,
        default: 'Klik op datums om ze aan of uit te zetten. Meerdere selecties zijn mogelijk.',
    },
    minDate: {
        type: String,
        default: null,
    },
})

const emit = defineEmits(['update:modelValue', 'close'])

const draftDates = ref([])

const normalizeDates = (value) => {
    if (!Array.isArray(value)) {
        return []
    }
    return [...new Set(value.filter(Boolean))].sort((a, b) => compareCalendarDates(a, b))
}

const normalizedDraftDates = computed(() => normalizeDates(draftDates.value))
const hasMinimumSelection = computed(() => normalizedDraftDates.value.length >= 2)

watch(() => props.show, (isOpen) => {
    if (!isOpen) {
        return
    }
    draftDates.value = normalizeDates(props.modelValue)
}, { immediate: true })

const confirmSelection = () => {
    if (!hasMinimumSelection.value) {
        return
    }

    emit('update:modelValue', normalizedDraftDates.value)
    emit('close')
}
</script>

<template>
    <ModalBase
        v-if="show"
        :title="title"
        body-class="multi-date-modal-body"
        footer-class="multi-date-modal-footer"
        @close="emit('close')"
    >
        <template #default>
            <div class="form-stack form-stack-sm">
                <CalendarPicker
                    :model-value="draftDates"
                    multiple
                    :min-date="minDate !== null ? minDate : todayIso"
                    @update:modelValue="draftDates = $event"
                />
                <small class="muted">{{ helpText }}</small>
                <small v-if="!hasMinimumSelection" class="muted">
                    Kies minimaal 2 datums om te bevestigen.
                </small>
            </div>
        </template>
        <template #footer>
            <div class="multi-date-modal-footer-actions">
                <button class="button button-secondary" type="button" @click="emit('close')">
                    Annuleren
                </button>
                <button class="button button-primary" type="button" :disabled="!hasMinimumSelection" @click="confirmSelection">
                    Bevestigen
                </button>
            </div>
        </template>
    </ModalBase>
</template>

<style scoped>
:deep(.multi-date-modal-body) {
    padding-bottom: var(--sp-4);
}

:deep(.multi-date-modal-footer) {
    padding-top: var(--sp-3);
    padding-bottom: var(--sp-3);
    padding-left: var(--sp-4);
    padding-right: var(--sp-4);
}

.multi-date-modal-footer-actions {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
}
</style>
